// Which Stytch project this browser talks to (#11778).
//
// A shared build knows at build time: VITE_ENV=prod uses the live tokens,
// anything else the test ones. A VITE_ENV=tenant build cannot — one image
// serves every tenant domain, and each tenant runs its own Stytch project — so
// it asks its own origin once at boot (`GET /api/v2/auth/stytch`, the same
// same-origin rule every tenant endpoint follows) and every Stytch client
// built afterwards uses the answer.
//
// A tenant that cannot load its tokens renders an error instead of falling
// back to a build-time token: falling back would sign real users into the
// shared *test* Stytch project, which is the defect this module removes.
//
// The same answer carries the tenant's login policy (#13595): `sso` when the
// tenant's login page offers SSO only, `all` for the shared login page. A
// shared build is always `all`, and so is a tenant backend that predates the
// field; any other value fails the load like missing tokens do.
//
// An `sso` tenant also publishes the one SAML connection its organization
// signs in through, so its login page can start SSO without asking for an
// email. A shared build and a backend that predates the field publish none.
//
// It also says whether the deployment reviews PHI documents (#11422 round 19,
// R8): there the app's /documents is the intake queue, decided before sign-in
// so the loading placeholder is the queue's too. A shared build never asks
// and is never one. A local build asks its own backend once, which answers
// only with FP_LOCAL_PHI_DOCUMENT_REVIEW=1 (`.claude/rules/local-fault-knobs.md`);
// any other answer, or none, is not one.

import { BASE_API_URL_V2 } from "./constants";

export type LoginMethods = "sso" | "all";

type Loaded = {
  b2c: string;
  b2b: string;
  loginMethods: LoginMethods;
  ssoConnectionId: string | null;
  phiDocumentReview: boolean;
};

const IS_TENANT_BUILD = import.meta.env.VITE_ENV === "tenant";
const IS_LOCAL_BUILD = import.meta.env.VITE_ENV === "local";

const buildTimeTokens = (): Loaded =>
  import.meta.env.VITE_ENV === "prod"
    ? {
        b2c: import.meta.env.VITE_REACT_APP_STYTCH_PUBLIC_TOKEN,
        b2b: import.meta.env.VITE_REACT_APP_STYTCH_B2B_PUBLIC_TOKEN,
        loginMethods: "all",
        ssoConnectionId: null,
        phiDocumentReview: false,
      }
    : {
        b2c: import.meta.env.VITE_REACT_APP_STYTCH_PUBLIC_TOKEN_TEST,
        b2b: import.meta.env.VITE_REACT_APP_STYTCH_B2B_PUBLIC_TOKEN_TEST,
        loginMethods: "all",
        ssoConnectionId: null,
        phiDocumentReview: false,
      };

/** How long a local build waits for its backend's answer before rendering
 *  without it: the app renders only after this load, so a hung local backend
 *  must not hold the page. */
const LOCAL_PROBE_TIMEOUT_MS = 2000;

/** A local build's answer from its own backend: PHI document review only
 *  when the backend says so. A 404 (the knob is off), an unreachable or slow
 *  backend, or anything unreadable is not. */
const localPhiDocumentReview = async (): Promise<boolean> => {
  try {
    const response = await fetch(`${BASE_API_URL_V2}/api/v2/auth/stytch`, {
      signal: AbortSignal.timeout(LOCAL_PROBE_TIMEOUT_MS),
    });
    if (!response.ok) return false;
    const body = (await response.json()) as { phi_document_review?: unknown };
    return body.phi_document_review === true;
  } catch {
    return false;
  }
};

let loaded: Loaded | null = IS_TENANT_BUILD ? null : buildTimeTokens();

/** Fetch this tenant's Stytch public tokens. A no-op on a shared build, but
 *  for a local build's one ask of its backend whether it reviews PHI
 *  documents. Rejects when a tenant's backend cannot supply them; the caller
 *  must not render a Stytch client after a rejection. */
export const loadStytchPublicTokens = async (): Promise<void> => {
  if (!IS_TENANT_BUILD) {
    loaded = IS_LOCAL_BUILD
      ? {
          ...buildTimeTokens(),
          phiDocumentReview: await localPhiDocumentReview(),
        }
      : buildTimeTokens();
    return;
  }
  let response: Response;
  try {
    response = await fetch(`${document.location.origin}/api/v2/auth/stytch`);
  } catch (cause) {
    throw new Error("could not reach this deployment for its Stytch tokens", {
      cause,
    });
  }
  if (!response.ok) {
    throw new Error(
      `this deployment did not return its Stytch tokens (HTTP ${response.status})`
    );
  }
  const body = (await response.json()) as {
    public_token?: string;
    b2b_public_token?: string;
    login_methods?: string;
    sso_connection_id?: unknown;
    phi_document_review?: unknown;
  };
  if (!body.public_token || !body.b2b_public_token) {
    throw new Error("this deployment returned no Stytch public tokens");
  }
  // A backend that predates the field publishes none: the shared page.
  const loginMethods = body.login_methods ?? "all";
  if (loginMethods !== "sso" && loginMethods !== "all") {
    throw new Error("this deployment returned an unknown login policy");
  }
  const ssoConnectionId = body.sso_connection_id ?? null;
  if (ssoConnectionId !== null && typeof ssoConnectionId !== "string") {
    throw new Error("this deployment returned an unreadable SSO connection");
  }
  // A backend that predates the field reviews no PHI documents.
  const phiDocumentReview = body.phi_document_review ?? false;
  if (typeof phiDocumentReview !== "boolean") {
    throw new Error("this deployment returned an unreadable PHI review flag");
  }
  loaded = {
    b2c: body.public_token,
    b2b: body.b2b_public_token,
    loginMethods,
    ssoConnectionId,
    phiDocumentReview,
  };
};

const require_ = <K extends keyof Loaded>(which: K): Loaded[K] => {
  if (loaded === null) {
    throw new Error(
      "Stytch public tokens have not loaded; loadStytchPublicTokens() must " +
        "resolve before a Stytch client is constructed"
    );
  }
  return loaded[which];
};

export const stytchPublicToken = (): string => require_("b2c");
export const stytchB2BPublicToken = (): string => require_("b2b");

/** The Stytch API host that matches the *project* in use.
 *
 *  The SSO start URL is built by hand against this host, and the host has to
 *  follow the project rather than the build: a tenant build is not
 *  `VITE_ENV=prod` yet runs on a live project, so keying off the build env
 *  would send it to test.stytch.com and fail every SSO start. Stytch prefixes
 *  a publishable token with its own environment, so the token answers it. */
export const stytchApiDomain = (): string =>
  require_("b2b").startsWith("public-token-live-")
    ? "api.stytch.com"
    : "test.stytch.com";

/** Which login methods this deployment's login page offers. */
export const tenantLoginMethods = (): LoginMethods => require_("loginMethods");

/** The SAML connection an SSO-only tenant's login page starts on its own, or
 *  null when the page must ask for an email to find one. */
export const tenantSsoConnectionId = (): string | null =>
  require_("ssoConnectionId");

/** Whether this deployment reviews PHI documents: the app's /documents is the
 *  intake queue. The app reads it once, at its root (`deploymentView.tsx`). */
export const phiDocumentReview = (): boolean => require_("phiDocumentReview");
